/**
 * F 233 — anchors: suggest, set, search.
 *
 * THE RULE THIS MODULE EXISTS TO ENFORCE
 * --------------------------------------
 * An anchor is plaintext a reader without the key can search. The AI proposes
 * anchors ONCE, from plaintext it was already given, and the user then edits,
 * accepts or rejects them. After that the AI searches anchors and NEVER
 * content.
 *
 * `suggestAnchors` PERSISTS NOTHING. That is the load-bearing property, not an
 * implementation detail: if generating a suggestion wrote it, then a suggestion
 * the user never accepted would already be a stored, searchable anchor — and
 * the "the user curates what the AI can see" promise would be false before the
 * user had done anything. It returns candidates; only `setAnchors` writes.
 *
 * Anchors are stored `source: 'ai'` when they are an accepted suggestion, so
 * provenance is honest and a later reader can tell a typed label from an
 * inferred one.
 */

import type { Anchor } from '../types/models/BaseEntity';
import { readNode } from './credentials/vault';

export type { Anchor };

export interface AnchorResult<T = void> {
  ok: boolean;
  error?: string;
  value?: T;
}

const ok = <T,>(value: T): AnchorResult<T> => ({ ok: true, value });
const fail = (error: string): AnchorResult<never> => ({ ok: false, error });

export interface AnchorNode {
  id: string;
  anchors?: Anchor[];
  content?: unknown;
  encrypted?: boolean;
  [key: string]: unknown;
}

export interface AnchorStore {
  get(id: string): Promise<AnchorNode | undefined>;
  set(id: string, node: AnchorNode): Promise<void>;
}

// ─── Suggestion ───────────────────────────────────────────────────────────

/** Words too common to be a useful hint. Not a stopword list — a floor. */
const NOISE = new Set([
  'the', 'and', 'for', 'that', 'this', 'with', 'from', 'have', 'has', 'was',
  'were', 'are', 'but', 'not', 'you', 'your', 'our', 'its', 'it', 'a', 'an',
  'of', 'to', 'in', 'on', 'is', 'be', 'as', 'at', 'by', 'or', 'if', 'so',
  'do', 'does', 'did', 'can', 'will', 'would', 'should', 'could',
]);

export interface SuggestOptions {
  /** Anchor kind to assign. Default 'hint'. */
  kind?: Anchor['kind'];
  /** Cap on returned candidates. Default 5. */
  limit?: number;
  /** ISO timestamp to stamp. Defaults to now; injectable so tests are stable. */
  now?: () => string;
}

/**
 * Propose anchor candidates for a node, from its plaintext.
 *
 * REQUIRES AN OPEN VAULT. On a locked node this returns an error rather than
 * guessing from ciphertext — a suggestion derived from a blob would be noise,
 * and the temptation to "decrypt a bit to be helpful" is exactly the door this
 * design closes.
 *
 * Persists NOTHING.
 */
export async function suggestAnchors(
  entityId: string,
  store: AnchorStore,
  opts: SuggestOptions = {},
): Promise<AnchorResult<Anchor[]>> {
  if (!entityId) return fail('entityId is required');
  const node = await store.get(entityId);
  if (!node) return fail(`no node with id "${entityId}"`);

  const read = await readNode(entityId);
  if (read.locked) {
    return fail('vault is locked — anchors are suggested from plaintext, and there is none to read');
  }
  const text = stringify(read.content);
  if (!text.trim()) return ok([]);

  const kind = opts.kind ?? 'hint';
  const limit = opts.limit ?? 5;
  const stamp = (opts.now ?? (() => new Date().toISOString()))();

  const seen = new Set<string>();
  const out: Anchor[] = [];
  // Longest first: a multi-word phrase is a better hint than either word.
  const candidates = text
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !NOISE.has(w.toLowerCase()));

  for (const word of candidates) {
    const key = word.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: `${entityId}:${key}`,
      text: word,
      kind,
      source: 'ai',
      confidence: Number((1 / Math.max(1, word.length / 6)).toFixed(2)),
      createdAt: stamp,
    });
    if (out.length >= limit) break;
  }
  return ok(out);
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// ─── Set (the only writer) ────────────────────────────────────────────────

/**
 * Persist the user's anchor set.
 *
 * This is the ONLY function in F 233 that writes anchors, which is what makes
 * "the AI cannot add a searchable hint on its own" true by construction rather
 * than by review.
 *
 * Validation is deliberately forgiving of provenance and strict about text:
 * an anchor with no searchable text is not an anchor, and silently storing one
 * would make `searchByAnchors` quietly return rows nobody can explain.
 */
export async function setAnchors(
  entityId: string,
  anchors: Anchor[],
  store: AnchorStore,
): Promise<AnchorResult<Anchor[]>> {
  if (!entityId) return fail('entityId is required');
  const node = await store.get(entityId);
  if (!node) return fail(`no node with id "${entityId}"`);
  if (!Array.isArray(anchors)) return fail('anchors must be an array');

  const clean: Anchor[] = [];
  for (const [i, a] of anchors.entries()) {
    if (!a || typeof a.text !== 'string' || a.text.trim() === '') {
      return fail(`anchors[${i}] has no text — an anchor without text is not searchable`);
    }
    if (a.kind !== 'label' && a.kind !== 'tag' && a.kind !== 'hint') {
      return fail(`anchors[${i}].kind must be 'label' | 'tag' | 'hint'`);
    }
    if (a.source !== 'user' && a.source !== 'ai') {
      return fail(`anchors[${i}].source must be 'user' | 'ai'`);
    }
    if (a.confidence !== undefined && typeof a.confidence !== 'number') {
      return fail(`anchors[${i}].confidence must be a number when present`);
    }
    clean.push({
      id: typeof a.id === 'string' && a.id ? a.id : `${entityId}:a${i}`,
      text: a.text.trim(),
      kind: a.kind,
      source: a.source,
      ...(a.confidence !== undefined ? { confidence: a.confidence } : {}),
      createdAt: typeof a.createdAt === 'string' && a.createdAt
        ? a.createdAt
        : new Date().toISOString(),
    });
  }

  // STRUCTURE IS PRESERVED. Only `anchors` changes; `content` is written back
  // exactly as read, so setting anchors on a sealed node does not disturb the
  // ciphertext.
  await store.set(entityId, { ...node, anchors: clean });
  return ok(clean);
}

// ─── Search ───────────────────────────────────────────────────────────────

export interface AnchorMatch {
  id: string;
  /** Which anchor text matched. */
  matched: string;
  anchors: Anchor[];
  /** The structure the reader is allowed to see. Never the content. */
  node: Omit<AnchorNode, 'content'>;
}

/**
 * Find nodes by anchor text.
 *
 * NEVER READS `content`. It is not passed a store, it is handed nodes, and all
 * it does with them is look at `anchors`. A caller that wants content in the
 * result has to go and get it — through a vault-gated path, deliberately.
 */
export function searchByAnchors(
  query: string,
  nodes: readonly AnchorNode[],
): AnchorMatch[] {
  if (typeof query !== 'string' || query.trim() === '') return [];
  const q = query.trim().toLowerCase();
  const out: AnchorMatch[] = [];
  for (const n of nodes) {
    const anchors = Array.isArray(n.anchors) ? n.anchors : [];
    const hit = anchors.find(
      (a) => typeof a?.text === 'string' && a.text.toLowerCase().includes(q));
    if (!hit) continue;
    // `content` is stripped on the way out. Not omitted by the caller —
    // removed here, so a caller cannot leak it by spreading the node.
    const { content: _content, ...structure } = n;
    out.push({ id: n.id, matched: hit.text, anchors, node: structure });
  }
  return out;
}
